import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updatePassword,
  EmailAuthProvider,
  reauthenticateWithCredential,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../firebase';
import api, { ApiError } from './api';

class AuthService {
  constructor() {
    this.userKey = 'user';
    this.companyKey = 'company';
    this.authStateKey = 'authInitialized';
  }

  // 🔒 SECURE: Register with Firebase first, then sync profile with backend
  async register(registrationData) {
    try {
      const { email, password, ...profileData } = registrationData;
      
      // Step 1: Create user in Firebase (secure password handling)
      const firebaseUserCredential = await createUserWithEmailAndPassword(auth, email, password);
      const firebaseUser = firebaseUserCredential.user;
      
      // Step 2: Get Firebase JWT token (no passwords involved)
      const token = await firebaseUser.getIdToken();
      
      try {
        // Step 3: Send profile data + Firebase UID to backend (no passwords)
        const response = await api.post('/users/complete-profile', {
          firebaseUid: firebaseUser.uid,
          email: firebaseUser.email,
          ...profileData
        });

        const { user: userData, company } = response;

        this.storeUserData(userData);
        this.storeCompanyData(company);
        this.setAuthInitialized(true);

        return {
          success: true,
          firebaseUser,
          userData,
          company,
          token
        };
      } catch (backendError) {
        // If backend fails, clean up Firebase user to avoid orphaned accounts
        console.warn('Backend profile creation failed, cleaning up Firebase user');
        try {
          await firebaseUser.delete();
        } catch (deleteError) {
          console.error('Failed to clean up Firebase user:', deleteError);
        }
        throw backendError;
      }
    } catch (error) {
      if (error.code && error.code.startsWith('auth/')) {
        throw new ApiError(
          this.getFirebaseErrorMessage(error.code),
          400,
          error.code
        );
      }
      
      if (error instanceof ApiError) {
        throw error;
      }
      
      throw new ApiError(
        error.message || 'Registration failed',
        error.status || 500,
        error.code || 'REGISTRATION_ERROR'
      );
    }
  }

  // 🔒 SECURE: Firebase-only authentication, then fetch profile
  async signIn(email, password) {
    try {
      // Step 1: Authenticate with Firebase directly (secure)
      const firebaseUserCredential = await signInWithEmailAndPassword(auth, email, password);
      const firebaseUser = firebaseUserCredential.user;
      
      // Step 2: Get Firebase JWT token
      const token = await firebaseUser.getIdToken();

      // Step 3: Fetch user profile from backend using Firebase UID (no passwords)
      try {
        const firebaseUid = firebaseUser.uid;
        const response = await api.get('/users/profile', {
          params: { firebaseUid },
          headers: { uid: firebaseUid }
        });
        const { user: userData, company } = response;

        this.storeUserData(userData);
        this.storeCompanyData(company);
        this.setAuthInitialized(true);

        return {
          success: true,
          firebaseUser,
          userData,
          company,
          token
        };
      } catch (profileError) {
        // If profile doesn't exist, user needs to complete registration
        if (profileError.status === 404) {
          return {
            success: true,
            firebaseUser,
            userData: null,
            company: null,
            token,
            requiresProfileCompletion: true
          };
        }
        throw profileError;
      }
    } catch (error) {
      if (error.code && error.code.startsWith('auth/')) {
        throw new ApiError(
          this.getFirebaseErrorMessage(error.code),
          401,
          error.code
        );
      }
      
      if (error instanceof ApiError) {
        throw error;
      }
      
      throw new ApiError(
        error.message || 'Login failed',
        error.status || 401,
        error.code || 'LOGIN_ERROR'
      );
    }
  }

  // 🔒 SECURE: Firebase-only logout, optional backend notification
  async signOut() {
    try {
      // Optional: Notify backend for cleanup (no sensitive data)
      try {
        await api.post('/users/logout');
      } catch (error) {
        console.warn('Backend logout notification failed:', error);
        // Don't fail logout if backend notification fails
      }

      // Primary logout: Firebase handles session termination
      await signOut(auth);
      this.clearAuthState();

      return { success: true };
    } catch (error) {
      // Always clear local state even if logout fails
      this.clearAuthState();
      
      throw new ApiError(
        'Logout failed',
        500,
        'LOGOUT_ERROR'
      );
    }
  }

  // 🔒 SECURE: Firebase-only password reset (no backend involvement)
  async resetPassword(email) {
    try {
      const appUrl =
        process.env.REACT_APP_APP_URL ||
        (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000');

      const actionCodeSettings = {
        url: appUrl,
        handleCodeInApp: false
      };

      await sendPasswordResetEmail(auth, email, actionCodeSettings);
      return { success: true };
    } catch (error) {
      if (error.code && error.code.startsWith('auth/')) {
        throw new ApiError(
          this.getFirebaseErrorMessage(error.code),
          400,
          error.code
        );
      }
      
      throw new ApiError(
        'Password reset failed',
        500,
        'PASSWORD_RESET_ERROR'
      );
    }
  }

  // 🔒 SECURE: Firebase-only password update (no backend involvement)
  async updateUserPassword(currentPassword, newPassword) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new ApiError('No authenticated user', 401, 'NO_USER');
      }

      // Reauthenticate with current password
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      
      // Update password in Firebase
      await updatePassword(user, newPassword);

      return { success: true };
    } catch (error) {
      if (error.code && error.code.startsWith('auth/')) {
        throw new ApiError(
          this.getFirebaseErrorMessage(error.code),
          400,
          error.code
        );
      }
      
      if (error instanceof ApiError) {
        throw error;
      }
      
      throw new ApiError(
        'Password update failed',
        500,
        'PASSWORD_UPDATE_ERROR'
      );
    }
  }

  // 🔒 SECURE: Token-based profile fetching
  async getCurrentUserProfile() {
    try {
      const firebaseUid = this.resolveCurrentUid();
      if (!firebaseUid) {
        throw new ApiError('User identifier is not available', 400, 'NO_FIREBASE_UID');
      }

      console.log('📡 AUTH-SERVICE: Fetching user profile from /users/profile for UID:', firebaseUid);
      const response = await api.get('/users/profile', {
        params: { firebaseUid },
        headers: { uid: firebaseUid }
      });
      console.log('✅ AUTH-SERVICE: Profile response received:', response);
      
      const { user: userData, company } = response;
      
      this.storeUserData(userData);
      this.storeCompanyData(company);
      
      return { user: userData, company };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      throw new ApiError(
        'Failed to fetch user profile',
        500,
        'PROFILE_FETCH_ERROR'
      );
    }
  }

  // 🔒 SECURE: Firebase token refresh (no backend needed)
  async refreshToken() {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new ApiError('No authenticated user', 401, 'NO_USER');
      }

      const token = await user.getIdToken(true);
      return { token };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      throw new ApiError(
        'Token refresh failed',
        401,
        'TOKEN_REFRESH_ERROR'
      );
    }
  }

  // 🔒 SECURE: Update last login locally and optionally notify backend
  async updateLastLogin(uid) {
    try {
      const timestamp = new Date().toISOString();
      
      // Update local storage
      const userData = this.getStoredUserData();
      if (userData) {
        const updatedUserData = {
          ...userData,
          lastLogin: timestamp
        };
        this.storeUserData(updatedUserData);
      }

      // Optional: Update backend with last login (non-blocking)
      try {
        console.log('📡 AUTH-SERVICE: Updating last login on backend for user:', uid);
        await api.patch('/users/last-login', {
          timestamp,
          firebaseUid: uid
        });
        console.log('✅ AUTH-SERVICE: Last login updated successfully on backend');
      } catch (error) {
        console.warn('❌ AUTH-SERVICE: Failed to update last login on backend:', error);
      }
    } catch (error) {
      console.warn('Failed to update last login:', error);
    }
  }

  // 🔒 SECURE: Firebase token validation (no backend verification needed)
  async verifyToken() {
    try {
      const user = auth.currentUser;
      if (!user) {
        return { valid: false, error: 'No authenticated user' };
      }

      // Firebase automatically validates token on getIdToken()
      const token = await user.getIdToken();
      return { valid: true, token };
    } catch (error) {
      return { valid: false, error: error.message };
    }
  }

  // 🔒 SECURE: Complete user profile after Firebase registration
  async completeProfile(profileData) {
    try {
      const user = auth.currentUser;
      if (!user) {
        throw new ApiError('No authenticated user', 401, 'NO_USER');
      }

      const response = await api.post('/users/complete-profile', {
        firebaseUid: user.uid,
        email: user.email,
        ...profileData
      });

      const { user: userData, company } = response;

      this.storeUserData(userData);
      this.storeCompanyData(company);
      this.setAuthInitialized(true);

      return { user: userData, company };
    } catch (error) {
      if (error instanceof ApiError) {
        throw error;
      }
      
      throw new ApiError(
        'Failed to complete profile',
        500,
        'PROFILE_COMPLETION_ERROR'
      );
    }
  }

  resolveCurrentUid() {
    if (auth.currentUser?.uid) {
      return auth.currentUser.uid;
    }

    const storedUser = this.getStoredUserData();
    if (storedUser) {
      return storedUser.uid || storedUser.firebaseUid || storedUser.id || null;
    }

    try {
      const cachedUid = localStorage.getItem('firebaseUid');
      if (cachedUid) {
        return cachedUid;
      }
    } catch (error) {
      console.warn('Failed to read cached Firebase UID:', error);
    }

    return null;
  }

  storeUserData(userData) {
    try {
      localStorage.setItem(this.userKey, JSON.stringify(userData));
      if (userData?.uid || userData?.firebaseUid) {
        localStorage.setItem('firebaseUid', userData.uid || userData.firebaseUid);
      }
    } catch (error) {
      console.error('Failed to store user data:', error);
    }
  }

  getStoredUserData() {
    try {
      const userData = localStorage.getItem(this.userKey);
      return userData ? JSON.parse(userData) : null;
    } catch (error) {
      console.error('Failed to retrieve user data:', error);
      return null;
    }
  }

  storeCompanyData(companyData) {
    try {
      localStorage.setItem(this.companyKey, JSON.stringify(companyData));
    } catch (error) {
      console.error('Failed to store company data:', error);
    }
  }

  getStoredCompanyData() {
    try {
      const companyData = localStorage.getItem(this.companyKey);
      return companyData ? JSON.parse(companyData) : null;
    } catch (error) {
      console.error('Failed to retrieve company data:', error);
      return null;
    }
  }

  setAuthInitialized(initialized) {
    try {
      localStorage.setItem(this.authStateKey, initialized.toString());
    } catch (error) {
      console.error('Failed to set auth initialized state:', error);
    }
  }

  isAuthInitialized() {
    try {
      return localStorage.getItem(this.authStateKey) === 'true';
    } catch (error) {
      console.error('Failed to check auth initialized state:', error);
      return false;
    }
  }

  initializeAuthState() {
    try {
      const userData = this.getStoredUserData();
      const companyData = this.getStoredCompanyData();
      const initialized = this.isAuthInitialized();
      
      return {
        userData,
        companyData,
        initialized
      };
    } catch (error) {
      console.error('Failed to initialize auth state:', error);
      return {
        userData: null,
        companyData: null,
        initialized: false
      };
    }
  }

  clearAuthState() {
    try {
      localStorage.removeItem(this.userKey);
      localStorage.removeItem(this.companyKey);
      localStorage.removeItem(this.authStateKey);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
    } catch (error) {
      console.error('Failed to clear auth state:', error);
    }
  }

  getCurrentUser() {
    return auth.currentUser;
  }

  isAuthenticated() {
    const firebaseUser = auth.currentUser;
    const userData = this.getStoredUserData();
    return !!(firebaseUser && userData);
  }

  getFirebaseErrorMessage(errorCode) {
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'No account found with this email address.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/email-already-in-use':
        return 'An account already exists with this email address.';
      case 'auth/weak-password':
        return 'Password is too weak. Please choose a stronger password.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection and try again.';
      case 'auth/requires-recent-login':
        return 'Please log in again to complete this action.';
      case 'auth/invalid-credential':
        return 'Invalid credentials. Please check your email and password.';
      default:
        return 'Authentication failed. Please try again.';
    }
  }

  getApiErrorMessage(error) {
    if (error instanceof ApiError) {
      return error.getUserFriendlyMessage();
    }
    
    return error.message || 'An unexpected error occurred.';
  }

  createAuthHeader() {
    const user = auth.currentUser;
    return user ? user.getIdToken() : null;
  }

  // Legacy method for backward compatibility
  onAuthStateChange(callback) {
    console.warn('onAuthStateChange is deprecated. Use Firebase onAuthStateChanged directly.');
    return onAuthStateChanged(auth, callback);
  }

  // Legacy method for backward compatibility  
  async signUp(email, password, userData) {
    console.warn('signUp is deprecated. Use register method instead.');
    return this.register({ email, password, ...userData });
  }

  // Legacy method for backward compatibility
  async getUserDocument(uid) {
    console.warn('getUserDocument is deprecated. Use getCurrentUserProfile method instead.');
    const profile = await this.getCurrentUserProfile();
    return profile.user;
  }
}

const authService = new AuthService();

export default authService;
